import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Option } from 'slim-select/dist/data.d';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import { RootState } from '../../../../core/state/state';
import Select from '../../../../components/form/select/Select';
import Input from '../../../../components/form/input/Input';
import { ExternalUserStatus } from '../../../../core/enums/ExternalUserStatus';
import { ProjectSubcontractor } from '../../../../core/state/subcontractors/types';
import {
  changeSubcontractorEmail,
  changeSubcontractorStatus,
  saveSubcontractorNotificationEmail,
  getExternalUsersByProjectId
} from '../../../../core/state/subcontractors/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { getExternalUsers } from '../../../../core/state/users/asyncActions';
import { Color } from '../../../../core/enums/Color';

import './AddExternalUserModal.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  handleSubcontractorModal: () => void;
}

type EditUserStatusFormData = {
  user: string;
  email: string;
  notificationsEmail: string;
  notificationsContactName: string;
};

const AddExternalUserModal: React.FC<Props> = ({ isOpen, closeModal, handleSubcontractorModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId } = useParams<{ projectId: string }>();
  const users = useSelector((state: RootState) =>
    state.subcontractor.projectSubcontractors?.filter((user) => user.statusEnum === ExternalUserStatus.Unblocked)
  );

  const usersToOption = (user: ProjectSubcontractor): Option => ({
    value: user.id.toString(),
    text: user.name
  });

  const userOptions = useMemo(() => users?.map(usersToOption), [users]);

  const validationSchema = yup.object().shape({
    user: yup.string().required(),
    email: yup.string().required(t('validation.email.required')).email(t('validation.email.invalidFormat')).min(2),
    notificationsEmail: yup.string().email(t('validation.email.invalidFormat')),
    notificationsContactName: yup.string()
  });

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { isValid, errors }
  } = useForm<EditUserStatusFormData>({
    mode: 'onChange',
    defaultValues: {
      user: '',
      email: '',
      notificationsEmail: '',
      notificationsContactName: ''
    },
    resolver: yupResolver(validationSchema)
  });

  const selectedUserId = watch('user');
  const setUserEmail = (userId: any) => {
    const newUser = users?.filter((user) => user.id === Number(userId));
    if (newUser && newUser.length > 0) {
      setValue('email', newUser[0].email, { shouldValidate: true });
    } else {
      setValue('email', '');
    }
  };

  const onSubmit = async (formData: EditUserStatusFormData) => {
    const subcontractorData = {
      notificationsEmail: formData.notificationsEmail || '',
      projectId: Number(projectId),
      notificationsContactName: formData.notificationsContactName || ''
    };

    await Promise.all([
      dispatch(changeSubcontractorEmail(Number(formData.user), formData.email)),
      dispatch(changeSubcontractorStatus(Number(formData.user), Number(projectId), ExternalUserStatus.Invited)),
      await dispatch(saveSubcontractorNotificationEmail(subcontractorData, Number(formData.user))),
      dispatch(
        getExternalUsersByProjectId(Number(projectId), [
          ExternalUserStatus.Active,
          ExternalUserStatus.Locked,
          ExternalUserStatus.Invited,
          ExternalUserStatus.Unblocked
        ])
      )
    ]);

    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Success,
        message: t('modal.manageSubcontractorRole.form.message.success')
      })
    );
    closeModal();
    dispatch(getExternalUsers());
  };

  const title = <h3 className="modal-title">{t('common.addNew')}</h3>;
  const buttons = (
    <div className="modal-buttons">
      <Button className="modal-buttons__left" color={Color.Blue} buttonTypes={['blue']} onClick={handleSubcontractorModal}>
        <span>{t('externalUsers.subcontractorRoles.button.createNew')}</span>
      </Button>
      <Button color={Color.Blue} buttonTypes={['transparent']} onClick={closeModal}>
        <span>{t('common.cancel')}</span>
      </Button>
      <Button disabled={!isValid} form="add-external-user" type="submit" buttonTypes={['blue']}>
        {t('common.save')}
      </Button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} title={title} toolbar={buttons} size="small" height={441}>
      <form id="add-external-user" className="add-external-user-modal" onSubmit={handleSubmit(onSubmit)}>
        <div className="add-external-user-modal__content">
          <Controller
            name="user"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <Select
                id="user"
                label={t('externalUsers.subcontractorRoles.form.subcontractor')}
                name={name}
                value={value}
                options={userOptions}
                onChange={(option) => {
                  if (Array.isArray(option)) {
                    [option] = option; // eslint-disable-line no-param-reassign
                  }
                  onChange(option.value);
                  setUserEmail(option.value);
                }}
                addPlaceholderOption
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                type="string"
                inputMode="email"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                id="email"
                error={errors.email?.message}
                name="email"
                infoText={`* (${t('common.forLogin')})`}
                label={t('common.email')}
                placeholder={t('common.placeholder.email')}
                value={value}
                disabled={selectedUserId !== 'undefined' && !!selectedUserId}
              />
            )}
          />
          <Controller
            name="notificationsContactName"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                type="string"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                id="notificationsContactName"
                error={errors.notificationsContactName?.message}
                name="notificationsContactName"
                label={`${t('common.contactPerson')} (${t('common.forNotifications')})`}
                placeholder={t('common.name')}
                value={value}
              />
            )}
          />
          <Controller
            name="notificationsEmail"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                type="string"
                inputMode="email"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                id="notificationsEmail"
                error={errors.email?.message}
                name="notificationsEmail"
                infoText={` (${t('common.forNotifications')})`}
                label={t('common.email')}
                placeholder={t('common.placeholder.email')}
                value={value}
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default AddExternalUserModal;
